import { SearchIcon, CameraIcon } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface AppSupportFAQ {
  q: string
  a: string
}

export interface AppSupportStep {
  title: string
  desc: string
}

export interface AppSupportData {
  slug: string
  name: string
  tagline: string
  icon: LucideIcon
  accentColor: string
  accentBg: string
  accentBorder: string
  shopifyUrl?: string
  productRoute: string
  description: string
  gettingStarted: AppSupportStep[]
  troubleshooting: AppSupportFAQ[]
  contact: {
    email: string
    responseTime: string
  }
}

export const APPS_SUPPORT: AppSupportData[] = [
  {
    slug: 'seo-suite',
    name: 'SEO Suite',
    tagline: 'Automated Shopify SEO & Rich Snippets',
    icon: SearchIcon,
    accentColor: 'text-emerald-400',
    accentBg: 'bg-emerald-500/10',
    accentBorder: 'border-emerald-500/30',
    shopifyUrl: 'https://apps.shopify.com/seo-suite',
    productRoute: '/seo-suite',
    description:
      'SEO Suite automates meta tags, image optimization, rich snippets (JSON-LD), and technical fixes to boost your organic traffic.',
    contact: {
      email: 'support@designflowdigitals.com',
      responseTime: 'within 24 hours',
    },
    gettingStarted: [
      {
        title: 'Install the app',
        desc: 'Add SEO Suite from the Shopify App Store and approve the required permissions.',
      },
      {
        title: 'Run the initial audit',
        desc: 'Once installed, the app will scan your store for missing meta tags, broken links, and unoptimized images. This takes 1-3 minutes.',
      },
      {
        title: 'Enable Auto-Pilot',
        desc: 'Turn on Auto-Pilot in the dashboard to have the app automatically generate meta titles and descriptions for new products.',
      },
      {
        title: 'Activate JSON-LD Rich Snippets',
        desc: 'Go to the Rich Snippets tab and toggle it on. This injects schema markup into your theme so Google can display product ratings and prices in search results.',
      },
      {
        title: 'Submit your Sitemap',
        desc: 'Copy your optimized sitemap URL from the app and submit it to Google Search Console to speed up indexing.',
      },
    ],
    troubleshooting: [
      {
        q: "Why aren't my new meta tags showing up on Google?",
        a: 'Google takes time to re-crawl your site. While the tags are updated instantly on your Shopify store, it can take anywhere from a few days to a few weeks for Google to reflect the changes in search results. You can speed this up by requesting indexing in Google Search Console.',
      },
      {
        q: 'Does the app modify my theme code?',
        a: "SEO Suite uses Shopify's modern App Blocks and Script Tags where possible. For JSON-LD, it safely injects the schema without permanently altering your core theme files. If you uninstall the app, the injected code is cleanly removed.",
      },
      {
        q: 'How does the image optimizer work?',
        a: 'The app compresses your product images to reduce file size without losing visible quality, and automatically adds descriptive ALT tags based on your product titles to help with Google Images ranking.',
      },
      {
        q: 'What happens if I uninstall the app?',
        a: 'If you uninstall, the automated features (like Auto-Pilot meta generation and JSON-LD injection) will stop working. However, any meta tags that were permanently saved to your Shopify products will remain intact.',
      },
      {
        q: 'How do I upgrade or downgrade my plan?',
        a: 'You can manage your subscription directly from the Billing tab inside the app, or through your Shopify admin under Settings > Apps and sales channels.',
      },
    ],
  },
  {
    slug: 'ai-photoshoot',
    name: 'AI Product Photoshoot',
    tagline: 'Studio-quality product scenes powered by AI',
    icon: CameraIcon,
    accentColor: 'text-purple-400',
    accentBg: 'bg-purple-500/10',
    accentBorder: 'border-purple-500/30',
    shopifyUrl: 'https://apps.shopify.com/ai-product-photoshoot-dfd',
    productRoute: '/ai-photoshoot',
    description:
      'Transform plain product shots into high-converting lifestyle scenes in seconds using artificial intelligence.',
    contact: {
      email: 'support@designflowdigitals.com',
      responseTime: 'within 24 hours',
    },
    gettingStarted: [
      {
        title: 'Install the app',
        desc: 'Add AI Product Photoshoot from the Shopify App Store.',
      },
      {
        title: 'Select a product',
        desc: 'Choose a product from your Shopify catalog. For best results, use a photo with a clear, uncluttered background.',
      },
      {
        title: 'Remove the background',
        desc: 'Click the "Remove Background" button. The AI will automatically isolate your product perfectly.',
      },
      {
        title: 'Choose a scene preset',
        desc: 'Select from over 20+ professional scene presets (e.g., Minimalist Studio, Nature, Marble Countertop) or type a custom prompt.',
      },
      {
        title: 'Generate and publish',
        desc: 'Click Generate. Review the 4 variations provided, select your favorite, and click "Publish to Store" to instantly update your product page.',
      },
    ],
    troubleshooting: [
      {
        q: 'The AI distorted my product. How do I fix this?',
        a: 'Ensure you are using the "Preserve Product" setting. The AI should only replace the background and adjust lighting/shadows, keeping your actual product untouched. Also, ensure the original photo is high resolution.',
      },
      {
        q: 'Why is the background removal cutting off parts of my product?',
        a: 'This usually happens if the product color closely matches the original background. Try uploading a photo with higher contrast between the product and the background.',
      },
      {
        q: 'Can I use my own custom prompts?',
        a: 'Yes! While we offer 20+ optimized presets, you can use the "Custom Prompt" tab to describe exactly the scene you want (e.g., "resting on a wooden table next to a cup of coffee, morning sunlight").',
      },
      {
        q: 'Where are the generated images saved?',
        a: 'Images are saved in your app gallery. When you click "Publish", they are automatically added to your Shopify product media library.',
      },
      {
        q: 'How do credits work?',
        a: 'One credit equals one generation (which produces 4 image variations). Credits refill monthly based on your subscription plan.',
      },
    ],
  },
]

export function getAppSupportBySlug(
  slug: string | undefined,
): AppSupportData | undefined {
  if (!slug) return undefined
  return APPS_SUPPORT.find((app) => app.slug === slug)
}
